import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useTranslation, Link } from "gatsby-plugin-react-i18next";

import * as styles from "../assets/sass/modules/_footer.module.scss";

const Footer = () => {
  const data = useStaticQuery(graphql`
    query ($language: String) {
      locales: allLocale(filter: { language: { eq: $language } }) {
        edges {
          node {
            ns
            data
            language
          }
        }
      }
      site: site {
        id
        siteMetadata {
          copyright
        }
      }
      featuredTechnologies: allMarkdownRemark(
        filter: {
          frontmatter: {
            locale: { eq: $language }
            tag: { eq: "technology" }
            pageDescription: { ne: null }
          }
        }
        sort: { frontmatter: { order: ASC } }
      ) {
        nodes {
          frontmatter {
            title
            slug
          }
        }
      }
      servicesCSD: allMarkdownRemark(
        filter: {
          frontmatter: {
            locale: { eq: $language }
            tag: { eq: "sub-service" }
            slug: { ne: null }
            parentUid: { eq: 900001 }
          }
        }
        sort: { frontmatter: { order: ASC } }
      ) {
        nodes {
          frontmatter {
            menuTitle
            slug
          }
          id
        }
      }
      servicesAT: allMarkdownRemark(
        filter: {
          frontmatter: {
            locale: { eq: $language }
            tag: { eq: "sub-service" }
            slug: { ne: null }
            parentUid: { eq: 900002 }
          }
        }
        sort: { frontmatter: { order: ASC } }
      ) {
        nodes {
          frontmatter {
            menuTitle
            slug
          }
          id
        }
      }
    }
  `);

  const { t } = useTranslation();

  const featuredTechnologies = data.featuredTechnologies.nodes;
  const servicesCSD = data.servicesCSD.nodes;
  const servicesAT = data.servicesAT.nodes;
  const copyright = data.site.siteMetadata.copyright;

  return (
    <footer className={styles.footer}>
      <div className="container">
        <div className="row">
          <div className="col-12 col-md-6 col-lg-2">
            <img src="/images/logo-dark.png" alt={t("LOGO_BW")} />
            <div className="m-top-2 m-bottom-2">
              <div className={styles.label}>{t("ADDRESS_LABEL_MAIN")}</div>
              <span className={styles.line}>{t("ADDRESS_MAIN_LINE_1")}</span>
              <span className={styles.line}>{t("ADDRESS_MAIN_LINE_2")}</span>
              <a href="tel:+35924372212">{t("ADDRESS_MAIN_PHONE")}</a>
            </div>
            <div className="m-top-2 m-bottom-2">
              <div className={styles.label}>{t("ADDRESS_LABEL_AMSTERDAM")}</div>
              <span className={styles.line}>
                {t("ADDRESS_AMSTERDAM_LINE_1")}
              </span>
              <span className={styles.line}>
                {t("ADDRESS_AMSTERDAM_LINE_2")}
              </span>
              <span className={styles.line}>
                {t("ADDRESS_AMSTERDAM_LINE_3")}
              </span>
              <span className={styles.line}>
                {t("ADDRESS_AMSTERDAM_LINE_4")}
              </span>
            </div>
            {/* <div className="m-bottom-4">
              <Link to={"/contact-us"} className="m-bottom-2">
                {t("LETS_TEAM_UP")}
              </Link>
              <Link to={"/careers"} className="m-bottom-2">
                {t("CHECK_POSITIONS")}
              </Link>
            </div> */}
          </div>
          <div className="col-12 col-md-6 col-lg-6">
            <h3>{t("SERVICES_AND_EXPERTISE")}</h3>
            <div className="row">
              <div className="col-12 col-md-6">
                <ul>
                  <li>
                    <Link
                      to="/services/innovation-advisory"
                      className="m-bottom-2"
                    >
                      {t("INNOVATION_ADVISORY")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/custom-software-development"
                      className="m-bottom-1"
                    >
                      {t("CUSTOM_SOFTWARE_DEVELOPMENT")}
                    </Link>
                    <ul>
                      {servicesCSD.map((item, index) => (
                        <li key={index}>
                          <Link to={item.frontmatter.slug}>
                            {item.frontmatter.menuTitle}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="col-12 col-md-6">
                <ul>
                  <li>
                    <Link
                      to="/services/team-augmentation"
                      className="m-bottom-2"
                    >
                      {t("TEAM_AUGMENTATION")}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/services/advanced-technologies"
                      className="m-bottom-1"
                    >
                      {t("ADVANCED_TECHNOLOGIES")}
                    </Link>
                    <ul>
                      {servicesAT.map((item, index) => (
                        <li key={index}>
                          <Link to={item.frontmatter.slug}>
                            {item.frontmatter.menuTitle}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="col-12 col-md-6 col-lg-2">
            <h3>{t("TECHNOLOGIES")}</h3>
            <ul>
              {featuredTechnologies.map((item, index) => (
                <li key={index}>
                  <Link to={item.frontmatter.slug} className="m-bottom-2">
                    {item.frontmatter.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/technologies" className="m-bottom-2">
                  {t("SEE_ALL_TECHNOLOGIES")}
                </Link>
              </li>
            </ul>
          </div>
          <div className="col-12 col-md-6 col-lg-2">
            <h3>{t("LEARN_MORE")}</h3>
            <ul>
              <li>
                <Link to="/our-work" className="m-bottom-2">
                  {t("OUR_WORK")}
                </Link>
              </li>
              <li>
                <Link to="/our-approach" className="m-bottom-2">
                  {t("OUR_APPROACH")}
                </Link>
              </li>
              <li>
                <Link to="/our-company" className="m-bottom-2">
                  {t("OUR_COMPANY")}
                </Link>
              </li>
              <li>
                <Link to="/our-team" className="m-bottom-2">
                  {t("OUR_TEAM")}
                </Link>
              </li>
              <li>
                <Link to={"/blog"} className="m-bottom-2">
                  {t("OUR_BLOG")}
                </Link>
              </li>
              <li>
                <div className="d-flex m-bottom-1">
                  <a
                    href="https://www.facebook.com/devisionbg/"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-facebook" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/1309343/"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-linkedin" />
                  </a>
                  <a
                    href="https://twitter.com/devisionbg"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-twitter" />
                  </a>
                  <a
                    href="https://www.youtube.com/@devision8339"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-youtube" />
                  </a>
                </div>
                <div className="d-flex m-bottom-2">
                  <a
                    href="https://podcasts.apple.com/us/podcast/devision-tech-talk/id1698128817"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-appleinc" />
                  </a>
                  <a
                    href="https://open.spotify.com/show/7a2dBTlSvxmfiMVTUvthVI?si=b83f3b7be6484e5e"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-spotify" />
                  </a>
                  <a
                    href="https://www.instagram.com/devision_ltd"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.social}
                  >
                    <i className="dei-instagram" />
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className={`${styles.copy} col-12 col-sm-6`}>{copyright}</div>
          <div className={`${styles.utilityLinks} col-12 col-sm-6`}>
            <Link
              to="/privacy-policy"
              className={`${styles.utilityLink} m-top-1 m-bottom-1`}
            >
              {t("PRIVACY_POLICY")}
            </Link>
            <Link
              to="/terms-and-conditions"
              className={`${styles.utilityLink} m-top-1 m-bottom-1`}
            >
              {t("TERMS_AND_CONDITIONS")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
